import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
// import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import ContractHoldDocs from '../../LeasingComponents/redesign/ContractHoldDocs';
import actions from 'src/actions';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import DialogList from 'src/dialogs';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../../LeasingComponents/redesign/ContractWorkingInterestList';
import PropertyUtil from 'src/util/PropertyUtil';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Ownership from 'src/models/tract_detail/Ownership';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
// import MathUtil from 'src/util/MathUtil';
import ProvisionLeaseClause from 'src/models/ProvisionLeaseClause';
import AgreementUtil from 'src/util/AgreementUtil';
import { IRootState } from 'src/reducers';
import AddOwnershipPages from '../AddOwnershipPages';
import OwnerUtil from 'src/util/OwnerUtil';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import LandContractType from 'src/models/LandContractType';
import ProvisionClause from 'src/components/common/ProvisionClause';
import ProvisioningUpdateDto from 'src/models/ProvisioningUpdateDto';
import CarbonCapture, { CarbonCaptureHold } from 'src/models/contract_types/CarbonCapture';

const CarbonCaptureInfo = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { get, put, post, response, del } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const dispatch = useDispatch();

    // REDUCERS
    const { project } = useSelector((state: IRootState) => state);

    const [carbonCaptureData, setCarbonCaptureData] = useState<CarbonCapture>(undefined);
    const [provisionClauseTypes, setProvisionClauseTypes] = useState<Array<ProvisionClauseType>>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>(undefined);
    const [attachedProvisions, setAttachedProvisions] = useState<Array<ProvisionLeaseClause>>(undefined);
    const [currentLeaseHold, setCurrentLeaseHold] = useState(undefined);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState(0);
    const [royalty, setRoyalty] = useState(0);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);
    const [isReadonly, setIsReadonly] = useState<boolean>(false);
    const [contractLookupId, setContractLookupId] = useState<number>(0);

    const { isDirty } = formState;

    useEffect(() => {
        getMeasurementTypes();
        getProvisionClauseTypes();
        if (appContext.carbonCaptureId > 0) {
            getCarbonCaptureData(appContext.carbonCaptureId);
        } else {
            setIsReadonly(false);
        }
    }, []);

    useEffect(() => {
        setDataLoaded(!!measurementTypes && !!attachedProvisions);
    }, [measurementTypes, attachedProvisions]);

    useEffect(() => {
        if (dataLoaded === true) {
            reset(prepData(carbonCaptureData, attachedProvisions));
        }
    }, [dataLoaded]);

    useEffect(() => {
        if (carbonCaptureData?.id > 0) {
            if (appContext.carbonCaptureHoldId > 0 && carbonCaptureData?.carbonCaptureHolds?.findIndex((slh) => slh.id === appContext.carbonCaptureHoldId) > -1) {
                const leaseHoldIndex = carbonCaptureData?.carbonCaptureHolds?.findIndex((slh) => slh.id === appContext.carbonCaptureHoldId);
                setCurrentLeaseHold(leaseHoldIndex);
                setRoyalty(carbonCaptureData?.carbonCaptureHolds?.[leaseHoldIndex]?.royalty);
            } else if (appContext.carbonCaptureHoldId === 0) {
                const tempCarbonCaptureData = carbonCaptureData;
                tempCarbonCaptureData.carbonCaptureHolds.push(new CarbonCaptureHold());
                setCarbonCaptureData(tempCarbonCaptureData);
                setCurrentLeaseHold(tempCarbonCaptureData?.carbonCaptureHolds?.length - 1);
                setRoyalty(0);
                setAttachedProvisions([]);
            }
        }
    }, [carbonCaptureData]);

    useEffect(() => {
        const id = carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.contractLookupId;
        if (id > 0) {
            setContractLookupId(id);
        }
    }, [currentLeaseHold]);

    useEffect(() => {
        if (contractLookupId > 0) {
            refreshProvisions();
        }
    }, [contractLookupId]);

    useEffect(() => {
        let formValues = getValues();
        if (attachedProvisions !== undefined) {
            formValues.provisionLeaseClauses = initialProvisions(attachedProvisions);
        }
        reset(formValues);
    }, [attachedProvisions]);

    // Disable Copy Leasehold button until Source Leasehold is selected
    useEffect(() => {
        setSourceSelected(false);
    }, [copyLeaseholdClicked]);

    const initializePage = (contractId: number = undefined) => {
        setDataLoaded(false);
        setCarbonCaptureData(undefined);
        setAttachedProvisions(undefined);
        setContractLookupId(undefined);
        setCurrentLeaseHold(undefined);
        getMeasurementTypes();
        getProvisionClauseTypes();
        getCarbonCaptureData(contractId ?? appContext.carbonCaptureId);
    };

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };

    const prepData = useCallback(
        (
            roughLeaseData: CarbonCapture,
            attachedProvisionClauses: ProvisionLeaseClause[]
        ) => {
            const formData: CarbonCapture = {
                ...roughLeaseData,
                reminderDate: roughLeaseData.reminderDate ? (moment(roughLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null,
                createdDate: roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                leaseDate: roughLeaseData.leaseDate ? (moment(roughLeaseData.leaseDate).format('YYYY-MM-DD') as unknown as Date)
                    : roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                effectiveDate: roughLeaseData.effectiveDate ? (moment(roughLeaseData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null,
                commitDate: roughLeaseData.commitDate ? (moment(roughLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null,
                inHandDate: roughLeaseData.inHandDate ? (moment(roughLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                originalInHandDate: roughLeaseData.originalInHandDate ? (moment(roughLeaseData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                voidDate: roughLeaseData.voidDate ? (moment(roughLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordedDate: roughLeaseData.recordedDate ? (moment(roughLeaseData.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null,

                currentHold: {
                    ...roughLeaseData.carbonCaptureHolds?.[currentLeaseHold],
                }
            };

            for (let provision of attachedProvisionClauses) {
                provision.obligationDate = provision.obligationDate ? (moment(provision.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                provision.obligationEndDate = provision.obligationEndDate ? (moment(provision.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
            }
            formData.currentHold.provisionLeaseClauses = attachedProvisionClauses;

            if (formData.isGeneratedLease === true && formData.isInHand === true) {
                setIsReadonly(true);
            } else {
                setIsReadonly(false);
            }

            return formData;
        },
        [currentLeaseHold, attachedProvisions, measurementTypes]
    );

    const initialProvisions = (data: ProvisionLeaseClause[]) => {
        for (let i = 0; i < data.length; i++) {
            data[i].obligationDate = data[i].obligationDate
                ? (moment(data[i].obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data[i].obligationEndDate = data[i].obligationEndDate
                ? (moment(data[i].obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }
        return data;
    };

    const getCarbonCaptureData = async (carbonCaptureId: number) => {
        const result: CarbonCapture = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/carbonCapture/${carbonCaptureId}`
        );
        if (response.ok) {
            setCarbonCaptureData(result);
        } else {
            window.alert('Get Carbon Capture Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const getProvisionClauseTypes = async () => {
        const result: Array<ProvisionClauseType> = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/types/${LandContractType.CarbonCapture}`);

        if (response.ok && Array.isArray(result)) {
            setProvisionClauseTypes(result ?? []);
        }
    };

    const refreshProvisions = async () => {
        const result: Array<ProvisionLeaseClause> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/agreements/${contractLookupId}`
        );
        if (response.ok) {
            setAttachedProvisions(result);
        }
    };

    const updateProvisionData = async (provisions: ProvisionLeaseClause[]) => {
        let attachedIds = provisions.map(plc => plc.id);
        const provisionDto: ProvisioningUpdateDto = {
            provisionLeaseClauses: provisions,
            provisionDeletes: (attachedProvisions ?? []).map(ap => ap.id).filter(ap => attachedIds.indexOf(ap) === -1),
            contractLookupIds: [contractLookupId]
        };
        await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause`, provisionDto);

        if (!response.ok) {
            window.alert('Unable to Update Provisions');
        }
    };

    const updateCarbonCapture = async (data: CarbonCapture) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/carbonCapture`,
            data
        );

        if (response.ok) {
            initializePage();
            if (appContext.carbonCaptureHoldId > 0) {
                dispatch(
                    push(window.location.pathname)
                );

            } else {
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${result.contractId}/carbon-capture-holds/${result.contractHoldId}`
                    )
                );
            }
        } else {
            window.alert('Update Carbon Capture failed');
        }
    };

    const deleteCarbonCaptureHold = (deleteParent: boolean) => {
        const confirmAction = async () => {
            const deleteId = carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold].id;
            let currentPath = window.location.pathname.split('/');
            let spliceLength = deleteParent === true ? 5 : 1;
            currentPath.splice(currentPath.length - spliceLength);
    
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/carbonCapture/${deleteId}/${deleteParent}`);
            if (response.ok) {
                if (!deleteParent) {
                    const switchToLeaseholdId = carbonCaptureData?.carbonCaptureHolds.find((x) => x.id !== carbonCaptureData?.carbonCaptureHolds?.[0]?.id)?.id;
                    getCarbonCaptureData(appContext.carbonCaptureId);
                    dispatch(
                        replace(
                            `${currentPath.join('/')}/${switchToLeaseholdId}`
                        )
                    );
                } else {
                    dispatch(
                        replace(
                            `${currentPath.join('/')}`
                        )
                    );
                }
            } else {
                window.alert('delete leasehold failed');
            }
        };

        let warning = deleteParent
            ? 'This is the only leasehold. The lease will also be deleted. Continue?'
            : 'Are you sure you want to delete this Leasehold?';

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>{warning}</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createCarbonCapture = async (data: CarbonCapture) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/carbonCapture`,
            data
        );
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/carbon-capture-holds/${result.contractHoldId}`
                )
            );
            initializePage(result.contractId);
        } else {
            window.alert('Create Carbon Capture failed');
        }
    };

    const onSubmit = async (data: CarbonCapture) => {
        data = finalizeCarbonCapture(data);
        if (data.id > 0) {
            if (contractLookupId > 0 && data.currentHold !== undefined) {
                await updateProvisionData(data.currentHold.provisionLeaseClauses);
            }
            updateCarbonCapture(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.originalInHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createCarbonCapture(data);
        }
    };
    
    const finalizeCarbonCapture = (data: CarbonCapture) => {
        data.id = carbonCaptureData?.id ? parseInt(carbonCaptureData?.id + '', 10) : 0;
        data.invoiceableLookupId = carbonCaptureData?.invoiceableLookupId ?? null;
        data.leaseNumber = carbonCaptureData?.leaseNumber ? carbonCaptureData?.leaseNumber : 0;
        data.rteLegalDescription = carbonCaptureData?.rteLegalDescription ? carbonCaptureData.rteLegalDescription : '';
        data.clientDocNumber = data.clientDocNumber ? data.clientDocNumber : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.originalLessee = data.originalLessee ? data.originalLessee : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.ownersOnDocument = carbonCaptureData?.ownersOnDocument || '';
        data.lesseeName = carbonCaptureData?.lesseeName || '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';

        data.reminderDate = data.reminderDate
            ? (moment(data.reminderDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.commitDate = data.commitDate
            ? (moment(data.commitDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.inHandDate = data.inHandDate
            ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.originalInHandDate = data.originalInHandDate
            ? (moment(data.originalInHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.recordedDate = data.recordedDate
            ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        data.primaryTerm = data.primaryTerm ? parseInt(data.primaryTerm + '', 10) : 0;
        data.primaryTermTypeId = data.primaryTermTypeId ? parseInt(data.primaryTermTypeId + '', 10) : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : 0;
        data.optionTerm = data.optionTerm ? parseInt(data.optionTerm + '', 10) : null;
        data.optionTermTypeId = data.optionTermTypeId ? parseInt(data.optionTermTypeId + '', 10) : 0;
        data.optionAmountTypeId = data.optionAmountTypeId ? parseInt(data.optionAmountTypeId + '', 10) : 0;
        data.secondOptionTerm = data.secondOptionTerm ? parseInt(data.secondOptionTerm + '', 10) : null;
        data.secondOptionTermTypeId = data.secondOptionTermTypeId ? parseInt(data.secondOptionTermTypeId + '', 10) : 0;
        data.secondOptionAmountTypeId = data.secondOptionAmountTypeId ? parseInt(data.secondOptionAmountTypeId + '', 10) : 0;
        data.rentalPriceTypeId = data.rentalPriceTypeId ? parseInt(data.rentalPriceTypeId + '', 10) : null;
        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        data.modifiedByUserId = data.modifiedByUserId ? parseInt(data.modifiedByUserId + '', 10) : null;
        data.coalPriceTypeId = data.coalPriceTypeId ? parseInt(data.coalPriceTypeId + '', 10) : null;

        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : null;
        data.optionAmount = data.optionAmount ? parseFloat(data.optionAmount + '') : null;
        data.secondOptionAmount = data.secondOptionAmount ? parseFloat(data.secondOptionAmount + '') : null;
        data.bonusAmount = data.bonusAmount ? parseFloat(data.bonusAmount + '') : null;
        data.originalGrossAcres = data.originalGrossAcres ? parseFloat(data.originalGrossAcres + '') : null;
        data.coalPrice = data.coalPrice ? parseFloat(data.coalPrice + '') : null;
        data.rentalPrice = data.rentalPrice ? parseFloat(data.rentalPrice + '') : null;

        data.optionExercised = AgreementUtil.parseBool(data.optionExercised + '');
        data.secondOptionExercised = AgreementUtil.parseBool(data.secondOptionExercised + '');
        data.isRental = AgreementUtil.parseBool(data.isRental + '');

        if (!!carbonCaptureData) {
            data.reminderNote = carbonCaptureData?.reminderNote ? carbonCaptureData.reminderNote : '';
            data.lesseeAddressLine1 = carbonCaptureData?.lesseeAddressLine1 ? carbonCaptureData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = carbonCaptureData?.lesseeAddressLine2 ? carbonCaptureData.lesseeAddressLine2 : '';
            data.lesseeCity = carbonCaptureData?.lesseeCity ? carbonCaptureData.lesseeCity : '';
            data.lesseeState = carbonCaptureData?.lesseeState ? carbonCaptureData.lesseeState : '';
            data.lesseeZipCode = carbonCaptureData?.lesseeZipCode ? carbonCaptureData.lesseeZipCode : '';
            data.ownersAddressOnDocument = carbonCaptureData?.ownersAddressOnDocument ? carbonCaptureData.ownersAddressOnDocument : '';
            data.ownersSSNTaxIdOnDocument = carbonCaptureData?.ownersSSNTaxIdOnDocument ? carbonCaptureData.ownersSSNTaxIdOnDocument : '';
            data.ownersOnDocument = carbonCaptureData.ownersOnDocument ? carbonCaptureData.ownersOnDocument : '';
            data.lesseeName = carbonCaptureData.lesseeName ? carbonCaptureData.lesseeName : '';
            data.lesseeOwnerLookupId = carbonCaptureData?.lesseeOwnerLookupId ? parseInt(carbonCaptureData?.lesseeOwnerLookupId + '', 10) : null;

            data.isNotOriginalLessee = carbonCaptureData?.isNotOriginalLessee ? carbonCaptureData.isNotOriginalLessee : null;
            data.isGeneratedLease = carbonCaptureData?.isGeneratedLease ? carbonCaptureData.isGeneratedLease : false;
            data.isVoid = carbonCaptureData?.isVoid ? carbonCaptureData.isVoid : false;
            data.isInHand = carbonCaptureData?.isInHand ? carbonCaptureData.isInHand : false;
            data.isCommitted = carbonCaptureData?.isCommitted ? carbonCaptureData.isCommitted : false;

            data.voidDate = carbonCaptureData?.voidDate ? (moment(carbonCaptureData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = carbonCaptureData?.commitDate ? (moment(carbonCaptureData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = carbonCaptureData?.inHandDate ? (moment(carbonCaptureData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.originalInHandDate = carbonCaptureData?.originalInHandDate ? (moment(carbonCaptureData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.reminderDate = carbonCaptureData?.reminderDate ? (moment(carbonCaptureData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        if (data.currentHold !== undefined) {
            let formatted = new Array<CarbonCaptureHold>();
            formatted.push(finalizeCarbonCaptureHold(data.currentHold));
            data.carbonCaptureHolds = [...formatted];
        }

        data.isFiledByMemo = !!data.isFiledByMemo ? AgreementUtil.parseBool(data.isFiledByMemo + '') : null;

        if (data.currentHold !== undefined) {
            let formatted = new Array<CarbonCaptureHold>();
            formatted.push(finalizeCarbonCaptureHold(data.currentHold));
            data.carbonCaptureHolds = [...formatted];
        }

        return data;
    };

    const finalizeCarbonCaptureHold = (data: CarbonCaptureHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = project.currentProject?.id;
        data.carbonCaptureId = carbonCaptureData?.id ? carbonCaptureData.id : 0;
        data.holdNumber = data.holdNumber ? parseInt(data.holdNumber + '', 10) : null;
        data.contractLookupId = contractLookupId;

        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;

        data.isActive = data.isActive ? data.isActive : null;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;

        data.hbp = AgreementUtil.parseBool(data.hbp + '');
        data.hbu = AgreementUtil.parseBool(data.hbu + '');
        data.seismicDataReq = AgreementUtil.parseBool(data.seismicDataReq + '');

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;

        data.seismicDataReq = AgreementUtil.parseBool(data.seismicDataReq + '');

        if (data.provisionLeaseClauses?.length > 0) {
            data.provisionLeaseClauses = data.provisionLeaseClauses.map((plc) => {
                plc.id = plc.id ? parseInt(plc.id + '', 10) : 0;
                plc.contractLookupId = carbonCaptureData?.carbonCaptureHolds[currentLeaseHold]?.contractLookupId ?? null;
                plc.invoiceableLookupId = plc.invoiceableLookupId ? parseInt(plc.invoiceableLookupId + '', 10) : null;
                plc.contractAdHocClauseId = plc.contractAdHocClauseId ? parseInt(plc.contractAdHocClauseId + '', 10) : null;
                plc.leaseAdHocClauseId = plc.leaseAdHocClauseId ? parseInt(plc.leaseAdHocClauseId + '', 10) : null;
                plc.contractAddendumClauseId = plc.contractAddendumClauseId ? parseInt(plc.contractAddendumClauseId + '', 10) : null;
                plc.clauseTypeId = plc.clauseTypeId ? parseInt(plc.clauseTypeId + '', 10) : null;
                plc.frequency = plc.frequency ? parseInt(plc.frequency + '', 10) : null;
                plc.obligationDate = plc.obligationDate ? (moment(plc.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.obligationEndDate = plc.obligationEndDate ? (moment(plc.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.useClientApproved = plc.useClientApproved ? AgreementUtil.parseBool(plc.useClientApproved + '') : null;
                return plc;
            });
        } else {
            data.provisionLeaseClauses = data.provisionLeaseClauses ?? [];
        }

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.projectId + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const previousContractHold = () => {
        if (currentLeaseHold > 0) {
            const carbonCaptureHoldId = carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold - 1]?.id;
            setCurrentLeaseHold(currentLeaseHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/carbon-captures/${appContext.carbonCaptureId}/carbon-capture-holds/${carbonCaptureHoldId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentLeaseHold + 1 < carbonCaptureData?.carbonCaptureHolds?.length) {
            const carbonCaptureHoldId = carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold + 1]?.id;

            setCurrentLeaseHold(currentLeaseHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/carbon-captures/${appContext.carbonCaptureId}/carbon-capture-holds/${carbonCaptureHoldId}`
                )
            );
        }
    };

    // const calculateTotal = (
    //     measurementTypeId: number,
    //     paymentAmount: number,
    //     grossAcres: number,
    //     measurementTypesList: Array<IndexReferenceType>
    // ) => {
    //     let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

    //     return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    // };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = carbonCaptureData.carbonCaptureHolds[currentLeaseHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${LandContractType.CarbonCapture}`,
            values
        );

        if (response.ok) {
            getCarbonCaptureData(appContext.carbonCaptureId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getCarbonCaptureData(appContext?.carbonCaptureId);
        } else {
            window.alert('Delete Ownership failed.');
        }
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getCarbonCaptureData(appContext.carbonCaptureId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = carbonCaptureData.carbonCaptureHolds[currentLeaseHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            carbonCaptureData.carbonCaptureHolds[currentLeaseHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
            });
        }

        return ownership;
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const onCopyLeasehold = async () => {
        setAttachedProvisions(undefined);
        setDataLoaded(false);
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/carbonCapture/carbonCapture-hold/${appContext.carbonCaptureHoldId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);

            dispatch(
                replace(
                    `${currentPath.join('/')}/${result}`
                )
            );
            setCopyLeaseholdClicked(false);
            initializePage();
            getCarbonCaptureData(appContext.carbonCaptureId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (carbonCaptureData?.carbonCaptureHolds?.length > 1) {
            let holds = [...carbonCaptureData.carbonCaptureHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: CarbonCaptureHold) => {
                return { value: lh.id || 0, label: lh.holdNumber?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as CarbonCapture))}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="Carbon Capture" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Carbon Capture Number"
                            name="leaseNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Lease Date"
                            name="leaseDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Record Date"
                            name="recordDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFToggle label="Filed by Memorandum" name="isFiledByMemo" register={register} errors={errors} disabled={isReadonly} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="originalGrossAcres"
                            register={register}
                            type="number"
                            errors={errors}
                            step={0.0001}
                            isRequired={true}
                            disabled={isReadonly}
                        />

                        <RHFInputBox
                            label="Original Lessor"
                            name="originalLessor"
                            register={register}
                            type="text"
                            errors={errors}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Original Lessee"
                            name="originalLessee"
                            register={register}
                            type="text"
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox label="Lessor AKA" name="lessorAka" register={register} type="text" errors={errors} disabled={isReadonly} />

                        <RHFInputBox label="Leased By" name="leasedBy" register={register} type="text" errors={errors} disabled={isReadonly} />
                        <RHFInputBox label="Client Doc Number" name="clientDocNumber" register={register} type="text" errors={errors} />
                    </div>
                    <RHFLegend title="Carbon Capture Hold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentLeaseHold + 1} of ${carbonCaptureData?.carbonCaptureHolds?.length || 1}`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                            {carbonCaptureData?.carbonCaptureHolds?.[0]?.surfaceOwner && (
                                <div style={{ marginLeft: '2rem', fontSize: '1.5rem' }}>
                                    <span>Owner: </span>
                                    <span>{OwnerUtil.MapOwner(carbonCaptureData?.carbonCaptureHolds?.[0]?.surfaceOwner)?.ownerName() || 'N/A'}</span>
                                </div>
                            )}
                        </div>
                        {carbonCaptureData?.carbonCaptureHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{ textAlign: 'center', marginRight: '1rem' }}>
                                        <RHFSelect
                                            onChange={(e) => handleSourceSelected(e)}
                                            name="sourceLeaseholdId"
                                            label="Source Leasehold"
                                            options={leaseholdList()}
                                            register={register}
                                        />
                                        <Button
                                            style={{ marginTop: '1rem' }}
                                            disabled={!sourceSelected}
                                            text="Confirm Copy"
                                            intent="danger"
                                            onClick={onCopyLeasehold}
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.carbonCaptureHoldId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary"
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteCarbonCaptureHold(false)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {carbonCaptureData?.carbonCaptureHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteCarbonCaptureHold(true)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="form-row">
                        <RHFCheckbox
                            style={{ marginBottom: '2rem' }}
                            label={'Active Lease'}
                            name={'currentHold.isActive'}
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.carbonCaptureId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.projectId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.carbonCaptureHoldNumber" register={register} disabled={true} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Bonus Amount"
                            name="bonusAmount"
                            type="number"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="bonusTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Fee', 'acre')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Coal Price"
                            name="coalPrice"
                            type="number"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="coalPriceTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Fee', 'ton')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                    </div>

                    <div className="form-row">
                        <RHFToggle 
                            name="optionExercised" 
                            label="Option Exercised?" 
                            bodyStyle={{ width: '20%' }} 
                            register={register} 
                            errors={errors} 
                        />
                        <RHFInputBox
                            label="Option Amount"
                            name="optionAmount"
                            type="number"
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="optionAmountTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Fee', 'acre')}
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Option Term"
                            name="optionTerm"
                            type="number"
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="optionTermTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Time')}
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                    </div>
                    <div className="form-row">
                        <RHFToggle 
                            name="secondOptionExercised" 
                            label="Second Option Exercised?" 
                            bodyStyle={{ width: '20%' }} 
                            register={register} 
                            errors={errors} 
                        />
                        <RHFInputBox
                            label="Second Option Amount"
                            name="secondOptionAmount"
                            type="number"
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="secondOptionAmountTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Fee', 'acre')}
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Second Option Term"
                            name="secondOptionTerm"
                            type="number"
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="secondOptionTermTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Time')}
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                    </div>
                    <div className="form-row">
                        <RHFToggle 
                            name="isRental" 
                            label="Rental?" 
                            bodyStyle={{ width: '20%' }} 
                            register={register} 
                            errors={errors} 
                        />
                        <RHFInputBox
                            label="Rental Price"
                            name="rentalPrice"
                            type="number"
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFSelect
                            label=" "
                            name="rentalPriceTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Fee', 'acre')}
                            bodyStyle={{ width: '20%' }} 
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Operator"
                            name="carbonCaptureHolds[0].operator"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '16%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Api No"
                            name="carbonCaptureHolds[0].apiNo"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '16%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Unit Name"
                            name="carbonCaptureHolds[0].unitName"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '16%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Field Name"
                            name="carbonCaptureHolds[0].fieldName"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '16%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Fed/State Lease No"
                            name="carbonCaptureHolds[0].fedStateLeaseNo"
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '16%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFToggle 
                            name="seismicDataReq" 
                            label="Ratified / Seismic Data Required?" 
                            bodyStyle={{ width: '20%' }} 
                            register={register} 
                            errors={errors} 
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Royalty"
                            name="currentHold.royalty"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                            step={0.0001}
                        />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <RHFTextArea
                            label={''}
                            name="legalDescription"
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                            disabled={isReadonly}
                        />
                    </div>

                    <RHFLegend title="Pooling Information" />

                    <div className="form-row">
                        <RHFToggle label="HBP" name="currentHold.hbp" register={register} errors={errors} />
                        <RHFToggle label="HBU" name="currentHold.hbu" register={register} errors={errors} />
                        <RHFInputBox
                            label="Pooling Order Number"
                            name="currentHold.poolingOrderNumber"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFInputBox label="Operator" name="currentHold.operator" register={register} type="text" errors={errors} />
                        <RHFInputBox label="API #" name="currentHold.apiNo" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Well Name" name="currentHold.unitName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Field Name" name="currentHold.fieldName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Unit Name" name="currentHold.fedStateLeaseNo" register={register} type="text" errors={errors} />
                        <RHFToggle label="Ratified/Seismic Data Req?" name="currentHold.seismicDataReq" register={register} errors={errors} />
                    </div>

                    {attachedProvisions !== undefined && (
                        <>
                            <RHFLegend title="Provisions" />
                            <div style={{ margin: 'auto 2rem' }}>
                                <ProvisionClause
                                    attachedProvisionClauses={attachedProvisions}
                                    options={provisionClauseTypes}
                                    formPrefix="currentHold.provisionLeaseClauses"
                                    register={register}
                                    errors={errors}
                                    getValues={getValues}
                                    setValue={setValue}
                                    key="provisionClause"
                                />
                            </div>
                        </>
                    )}

                    <RHFLegend title="Document Management" />
                    {carbonCaptureData?.carbonCaptureHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <div style={{ minHeight: '20rem', marginTop: '2rem' }}>
                        <RHFLegend title="Contract Ownership" />
                        <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.wiOwners}
                            wiContractOwnerships={carbonCaptureData?.carbonCaptureHolds?.[currentLeaseHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                        />
                    </div>

                    <div style={{ marginTop: '5rem' }}>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CarbonCaptureInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/LeaseholdInfoComponents/CarbonCaptureInfo.tsx